import { memo } from "react";
import type { ReactElement } from "react";
import {
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import {
  IconDatabase,
  IconHome,
  IconMap,
  IconMap2,
  IconSettings,
  IconSparkles,
} from "@tabler/icons-react";
import { memoize } from "es-toolkit";
import { MenuType } from "./types/menu";
import { usePathAccountName } from "./hooks/router";
import { useIntl, type IntlShape } from "react-intl";
import { PageNotFound } from "./components/PageNotFound";
import { PublicDashboardLayout } from "./layouts/PublicDashboardLayout";
import { Overview } from "./routes/app/Overview";
import { accountMapRoutes, accountMapsListRoute, accountNewMapRoute } from "./routes/app/map/routes";
import { accountNewTilesetRoute, accountTilesetRoutes, accountTilesetsListRoute } from "./routes/app/tileset/routes";
import { accountDatasetsListRoute } from "./routes/app/dataset/routes";
import { accountContactRoutes } from "./routes/contact/routes";
import { adminProfileRoutes, userProfileRoutes } from "./routes/profile/routes";
import { accountAuthRoutes, adminAuthRoutes, userAuthRoutes } from "./routes/auth/routes";
import { accountsRoutes } from "./routes/app/admin/routes";
import { DashboardLayout } from "./layouts/DashboardLayout";
import { adminDashboardRoutes } from "./routes/admin/routes";
import { isFeatureEnabled } from "./utils/posthog";
import type { NavMenuOptions } from "./types/menu";
import type { ToPathOption } from "@tanstack/react-router";

const menu = memoize(({ accountName, intl, isDataCatalogEnabled }: { accountName: string; intl: IntlShape; isDataCatalogEnabled?: boolean }): NavMenuOptions => {
  return {
    items: [
      {
        title: intl.formatMessage({
          defaultMessage: "Overview",
          id: "navigation.overview",
        }),
        type: MenuType.Single,
        Icon: IconHome,
        to: "/$accountName",
        params: { accountName },
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Edit AI",
          id: "navigation.mapkaAI",
        }),
        type: MenuType.Single,
        Icon: IconSparkles,
        to: "/$accountName/maps/osm-liberty/ai",
        params: { accountName: "maputnik" },
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Maps",
          id: "navigation.maps",
        }),
        type: MenuType.Single,
        Icon: IconMap2,
        to: "/$accountName/maps",
        params: { accountName },
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Tilesets",
          id: "navigation.tilesets",
        }),
        type: MenuType.Single,
        Icon: IconMap,
        to: "/$accountName/tilesets",
        params: { accountName },
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Data",
          id: "navigation.data",
        }),
        type: MenuType.Single,
        Icon: IconDatabase,
        to: "/$accountName/datasets",
        hidden: !isDataCatalogEnabled,
        params: { accountName },
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Admin",
          id: "navigation.admin",
        }),
        prefix: "/$accountName/admin",
        type: MenuType.Nested,
        hideOnMobile: true,
        Icon: IconSettings,
        options: [
          {
            title: intl.formatMessage({
              defaultMessage: "Members",
              id: "navigation.members",
            }),
            type: MenuType.Single,
            to: "/$accountName/admin/members",
            params: { accountName },
          },
          {
            title: intl.formatMessage({
              defaultMessage: "Billing",
              id: "navigation.billing",
            }),
            type: MenuType.Single,
            to: "/$accountName/admin/billing",
            params: { accountName },
          },
          {
            title: intl.formatMessage({
              defaultMessage: "Usage",
              id: "navigation.usage",
            }),
            type: MenuType.Single,
            to: "/$accountName/admin/usage",
            params: { accountName },
          },
          {
            title: intl.formatMessage({
              defaultMessage: "Audit log",
              id: "navigation.auditLog",
            }),
            type: MenuType.Single,
            to: "/$accountName/admin/audit-log",
            params: { accountName },
          },
          {
            title: intl.formatMessage({
              defaultMessage: "Configurations",
              id: "navigation.configurations",
            }),
            type: MenuType.Single,
            to: "/$accountName/admin/configurations",
            params: { accountName },
          },
          {
            title: intl.formatMessage({
              defaultMessage: "API keys",
              id: "navigation.apiKeys",
            }),
            type: MenuType.Single,
            to: "/$accountName/admin/api-keys",
            params: { accountName },
          },
        ],
      },
    ].filter(m => !m.hidden),
  } as NavMenuOptions;
}, { getCacheKey: ({ accountName, intl, isDataCatalogEnabled }) => `${accountName}-${intl.locale}-${isDataCatalogEnabled}` });

const rootRoute = createRootRoute();

export const userRoutes = createRoute({
  getParentRoute: () => rootRoute,
  id: "user",
});
export const accountRoutes = createRoute({
  getParentRoute: () => rootRoute,
  path: "/$accountName",
});
export const adminRoutes = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
});

export const accountDashboardLayoutRoute = createRoute({
  getParentRoute: () => accountRoutes,
  id: "admin-layout",
  component: memo(() => {
    const accountName = usePathAccountName();
    const intl = useIntl();
    const isDataCatalogEnabled = isFeatureEnabled("enable-data-catalog");

    return (
      <DashboardLayout menu={menu({ accountName, intl, isDataCatalogEnabled })}>
        <Outlet />
      </DashboardLayout>
    );
  }),
});

const accountHomeRoute = createRoute({
  getParentRoute: () => accountDashboardLayoutRoute,
  path: "/",
  component: Overview,
});

const routeTree = rootRoute.addChildren([
  adminRoutes.addChildren([
    adminAuthRoutes,
    adminProfileRoutes,
    adminDashboardRoutes,
  ]),
  userRoutes.addChildren([
    userAuthRoutes,
    userProfileRoutes,
  ]),
  accountRoutes.addChildren([
    accountAuthRoutes,
    accountTilesetRoutes,
    accountMapRoutes,
    accountContactRoutes,
    accountDashboardLayoutRoute.addChildren([
      accountHomeRoute,
      accountsRoutes,
      accountTilesetsListRoute,
      accountDatasetsListRoute,
      accountMapsListRoute,
      accountNewTilesetRoute,
      accountNewMapRoute,
    ]),
  ]),
]);

export const router = createRouter({
  notFoundMode: "root",
  defaultNotFoundComponent: () => {
    return (
      <PublicDashboardLayout>
        <PageNotFound />
      </PublicDashboardLayout>
    );
  },
  routeTree,
  defaultPreload: "intent",
});

export type Router = typeof router;
export type RoutePath = ToPathOption;

declare module "@tanstack/react-router" {
  interface Register {
    router: Router;
  }
}

export const App = (): ReactElement => {
  return <RouterProvider router={router} />;
};
